import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {
    ArrowLeft,
    CheckCircleFill,
    XCircleFill,
    DashCircleFill,
    InfoCircle,
} from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import type { ProjectWithRepository } from '../types/project';
import RunConfigModal, { RunConfig } from '../components/executions/RunConfigModal';
import './TestExecutions.css';

const ACTIVE_DETAIL_POLL_MS = 5000;

/* ============================================================
   Types
   ============================================================ */

interface StepResult {
    step_id?: string | number;
    step_index?: number;
    action?: string;
    expected?: string;
    success?: boolean;
    message?: string;
    error?: string | null;
    screenshot_path?: string | null;
    is_assertion?: boolean;
}

interface TestResult {
    test_key?: string;
    title?: string;
    name?: string;
    /** Runner stores pass/fail as a boolean `pass` field */
    pass?: boolean;
    status?: string; // may be absent in runner output
    duration_ms?: number | null;
    error?: string | null;
    steps?: StepResult[] | string[];
}

interface RunSummary {
    total_tests?: number;
    passed?: number;
    failed?: number;
    pass_rate?: number;
    test_results?: TestResult[];
    [key: string]: any;
}

interface AdoSyncResult {
    test_key: string;
    ado_work_item_id?: number | null;
    ado_test_point_id?: number | null;
    ado_test_result_id?: number | null;
    outcome?: string | null;
    status: string;
    message?: string | null;
    screenshot_attached?: boolean;
}

interface AdoSyncRun {
    id?: number | null;
    ado_test_run_id?: number | null;
    status: string;
    ado_run_url?: string | null;
    error_message?: string | null;
    total_count?: number;
    processed_count?: number;
    synced_count?: number;
    skipped_count?: number;
    failed_count?: number;
    current_test_key?: string | null;
    current_action?: string | null;
    message?: string | null;
    results?: AdoSyncResult[];
}

interface ExecutionDetail {
    id: number;
    project_id: number;
    status: string;
    base_url: string | null;
    run_name: string | null;
    run_id: string | null;
    created_at: string;
    completed_at: string | null;
    summary: RunSummary | null;
    /** Per-test checkpoints returned while the execution is still running. */
    live_results?: RunSummary | null;
    run_config?: RunConfig | null;
    ado_sync?: AdoSyncRun | null;
}

/* ============================================================
   Helpers
   ============================================================ */

function resolvePass(t: TestResult): boolean | null {
    if (t.pass !== undefined) return t.pass;
    const s = t.status?.toLowerCase();
    if (s === 'passed' || s === 'pass') return true;
    if (s === 'failed' || s === 'fail') return false;
    return null;
}

function testStatusIcon(t: TestResult): React.ReactElement {
    const p = resolvePass(t);
    if (p === true)  return <CheckCircleFill className="exec-test-pass" />;
    if (p === false) return <XCircleFill className="exec-test-fail" />;
    return <DashCircleFill className="exec-test-skip" />;
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getAdoSyncProgress(sync: AdoSyncRun | null): number {
    if (!sync?.total_count) return 0;
    return Math.min(100, Math.round(((sync.processed_count ?? 0) / sync.total_count) * 100));
}

function isAdoSyncTerminal(status?: string | null): boolean {
    const normalized = (status || '').toLowerCase();
    return normalized !== '' && normalized !== 'syncing';
}

/* ============================================================
   Component
   ============================================================ */

function TestExecutionDetail(): React.ReactElement {
    const { projectId, jobId } = useParams<{ projectId: string; jobId: string }>();
    const navigate = useNavigate();

    const [project, setProject] = useState<ProjectWithRepository | null>(null);
    const [detail, setDetail] = useState<ExecutionDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedRow, setExpandedRow] = useState<number | null>(null);
    const [showRunConfig, setShowRunConfig] = useState(false);
    const [adoSyncing, setAdoSyncing] = useState(false);
    const [adoSyncError, setAdoSyncError] = useState<string | null>(null);
    const isAzureProject = (project?.project_type ?? '').toLowerCase() === 'azure_devops';

    useEffect(() => {
        if (!projectId) return undefined;
        let cancelled = false;

        const loadProject = async () => {
            try {
                const res = await apiClient.get<ProjectWithRepository>(
                    API_ENDPOINTS.PROJECT_DETAIL(projectId)
                );
                if (!cancelled) setProject(res.data);
            } catch {
                if (!cancelled) setProject(null);
            }
        };

        void loadProject();

        return () => { cancelled = true; };
    }, [projectId]);

    const loadDetail = useCallback(async (): Promise<ExecutionDetail> => {
        const jobRes = await apiClient.get<ExecutionDetail>(
            API_ENDPOINTS.PLAYWRIGHT_EXECUTION_DETAIL(projectId!, jobId!)
        );
        return jobRes.data;
    }, [jobId, projectId]);

    const loadSummary = useCallback(async (): Promise<RunSummary | null> => {
        const summaryRes = await apiClient.get<{ summary: RunSummary }>(
            API_ENDPOINTS.PLAYWRIGHT_EXECUTION_SUMMARY(projectId!, jobId!)
        );
        return summaryRes.data?.summary || null;
    }, [jobId, projectId]);

    const load = useCallback(async () => {
        if (!projectId || !jobId) return;
        try {
            setLoading(true);
            setError(null);
            const job = await loadDetail();

            // Try fetching run summary (may fail if run not completed)
            try {
                job.summary = await loadSummary();
            } catch {
                // summary not yet available — leave as null
            }

            setDetail(job);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to load execution detail');
        } finally {
            setLoading(false);
        }
    }, [jobId, loadDetail, loadSummary, projectId]);

    useEffect(() => { load(); }, [load]);

    const handleAdoSync = useCallback(async () => {
        if (!projectId || !jobId || !isAzureProject) return;
        setAdoSyncing(true);
        setAdoSyncError(null);
        try {
            const response = await apiClient.post<AdoSyncRun>(
                API_ENDPOINTS.PLAYWRIGHT_ADO_SYNC(projectId, jobId)
            );
            setDetail((prev) => prev ? { ...prev, ado_sync: response.data } : prev);
        } catch (err: any) {
            setAdoSyncError(err?.response?.data?.detail || err?.message || 'Failed to sync with Azure Test Plans');
        } finally {
            setAdoSyncing(false);
        }
    }, [isAzureProject, jobId, projectId]);

    useEffect(() => {
        if (!projectId || !jobId || !isAzureProject) return undefined;
        const status = (detail?.ado_sync?.status || '').toLowerCase();
        if (status !== 'syncing') return undefined;

        let active = true;
        const poll = async () => {
            try {
                const response = await apiClient.get<AdoSyncRun>(
                    API_ENDPOINTS.PLAYWRIGHT_ADO_SYNC(projectId, jobId)
                );
                if (!active) return;
                setDetail((prev) => prev ? { ...prev, ado_sync: response.data } : prev);
                if (isAdoSyncTerminal(response.data?.status)) {
                    setAdoSyncing(false);
                }
            } catch {
                // Leave the current progress visible; a later poll can recover.
            }
        };

        void poll();
        const handle = window.setInterval(poll, 2000);
        return () => {
            active = false;
            window.clearInterval(handle);
        };
    }, [detail?.ado_sync?.status, isAzureProject, jobId, projectId]);

    useEffect(() => {
        if (!detail) return undefined;
        const status = (detail.status || '').toLowerCase();
        if (status !== 'pending' && status !== 'running') return undefined;

        const handle = window.setInterval(async () => {
            try {
                const nextDetail = await loadDetail();
                setDetail((prev) => ({ ...(prev || nextDetail), ...nextDetail }));

                // The summary endpoint intentionally rejects active jobs. Live
                // results arrive with `nextDetail`; request the final summary only
                // once the job settles.
                const shouldRefreshSummary = nextDetail.status === 'completed';

                if (shouldRefreshSummary) {
                    try {
                        const nextSummary = await loadSummary();
                        setDetail((prev) => prev ? { ...prev, ...nextDetail, summary: nextSummary } : { ...nextDetail, summary: nextSummary });
                    } catch {
                        setDetail((prev) => prev ? { ...prev, ...nextDetail } : nextDetail);
                    }
                }
            } catch {
                // Keep the current UI responsive; next poll can recover.
            }
        }, ACTIVE_DETAIL_POLL_MS);

        return () => window.clearInterval(handle);
    }, [detail, loadDetail, loadSummary]);

    /* ---- Render ---- */
    const isCompleted = (detail?.status || '').toLowerCase() === 'completed';
    // The final summary is written only after the whole job finishes.  Until
    // then, render the completed test-case checkpoints returned by the job API.
    const displaySummary = detail?.summary ?? detail?.live_results ?? null;
    const adoSync = detail?.ado_sync ?? null;
    const adoStatus = adoSync?.status || 'not_synced';
    const adoSyncInProgress = adoStatus === 'syncing';
    const adoSyncPreviouslyAttempted = !!adoSync && adoStatus !== 'not_synced';
    const adoSyncButtonLabel = adoSyncInProgress
        ? 'Syncing...'
        : adoSyncPreviouslyAttempted
            ? 'Re-sync Azure Test Plans'
            : 'Sync to Azure Test Plans';
    const adoProgress = getAdoSyncProgress(adoSync);
    const adoHasProgress = !!adoSync && ((adoSync.total_count ?? 0) > 0 || adoSyncInProgress);
    const adoStatusLabel = adoStatus.replace(/_/g, ' ');
    const adoPanelTitle = adoStatus === 'syncing'
        ? 'Azure Test Plans sync in progress'
        : `Azure Test Plans sync ${adoStatusLabel}`;

    return (
        <div className="exec-page">
            {/* Header */}
            <div className="exec-page-header">
                <div className="exec-page-header__left">
                    <button
                        className="exec-back-btn"
                        onClick={() => navigate(`/projects/${projectId}/executions`)}
                        aria-label="Back to executions"
                    >
                        <ArrowLeft />
                    </button>
                    <div>
                        <h1 className="exec-page-title">
                            {detail?.run_name || 'Execution Detail'}
                        </h1>
                        <p className="exec-page-subtitle">
                            {detail?.base_url && <>Target: <code>{detail.base_url}</code> · </>}
                            Started {formatDate(detail?.created_at ?? null)}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {detail?.run_config && (
                        <OverlayTrigger
                            placement="left"
                            overlay={<Tooltip id="execution-run-config-tip">Run configuration</Tooltip>}
                        >
                            <button
                                type="button"
                                className="exec-icon-btn"
                                onClick={() => setShowRunConfig(true)}
                                aria-label="View run configuration"
                            >
                                <InfoCircle size={16} />
                            </button>
                        </OverlayTrigger>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="exec-loading">
                    <Spinner animation="border" size="sm" />
                    <span>Loading…</span>
                </div>
            ) : error ? (
                <Alert variant="danger" className="exec-error">{error}</Alert>
            ) : !detail ? null : (
                <>
                    {isAzureProject && (
                    <div className="exec-table-wrapper" style={{ marginBottom: 16, padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div>
                                <h2 className="exec-section-title" style={{ marginBottom: 4 }}>Azure Test Plans Sync</h2>
                                <div className="exec-base-url">
                                    Status: <strong>{adoStatusLabel}</strong>
                                    {adoSync?.ado_test_run_id ? <> · ADO Run #{adoSync.ado_test_run_id}</> : null}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {adoSync?.ado_run_url && (
                                    <a className="exec-link-btn" href={adoSync.ado_run_url} target="_blank" rel="noreferrer">
                                        Open ADO Run
                                    </a>
                                )}
                                <button
                                    type="button"
                                    className="exec-primary-btn"
                                    onClick={handleAdoSync}
                                    disabled={!isCompleted || adoSyncing || adoSyncInProgress}
                                >
                                    {adoSyncing ? 'Syncing...' : adoSyncButtonLabel}
                                </button>
                            </div>
                        </div>
                        {!isCompleted && (
                            <Alert variant="info" className="mt-3 mb-0 py-2">
                                Azure Test Plans sync is available after this Playwright execution completes.
                            </Alert>
                        )}
                        {adoSyncError && (
                            <Alert variant="danger" className="mt-3 mb-0 py-2">{adoSyncError}</Alert>
                        )}
                        {adoSync?.error_message && (
                            <Alert variant="danger" className="mt-3 mb-0 py-2">{adoSync.error_message}</Alert>
                        )}
                        {adoHasProgress && (
                            <div className={`exec-ado-sync-progress exec-ado-sync-progress--${adoStatus}`}>
                                <div className="exec-ado-sync-progress-head">
                                    <div>
                                        <div className="exec-ado-sync-progress-title">{adoPanelTitle}</div>
                                        <div className="exec-ado-sync-progress-message">
                                            {adoSync?.message
                                                || adoSync?.current_action
                                                || (adoStatus === 'syncing' ? 'Syncing Playwright results with Azure Test Plans...' : 'Azure Test Plans sync finished.')}
                                        </div>
                                        {(adoSync?.current_test_key || adoSync?.current_action) && adoStatus === 'syncing' && (
                                            <div className="exec-ado-sync-progress-current">
                                                {adoSync.current_action ? `${adoSync.current_action}` : 'Processing'}
                                                {adoSync.current_test_key ? ` · ${adoSync.current_test_key}` : ''}
                                            </div>
                                        )}
                                    </div>
                                    <strong>{adoSync?.processed_count ?? 0} / {adoSync?.total_count ?? 0}</strong>
                                </div>
                                <div className="exec-ado-sync-progress-track" aria-hidden="true">
                                    <div className="exec-ado-sync-progress-fill" style={{ width: `${adoProgress}%` }} />
                                </div>
                                <div className="exec-ado-sync-progress-meta">
                                    <span>{adoSync?.synced_count ?? 0} synced</span>
                                    <span>{adoSync?.skipped_count ?? 0} skipped</span>
                                    <span>{adoSync?.failed_count ?? 0} failed</span>
                                </div>
                            </div>
                        )}
                        {adoSync?.results && adoSync.results.length > 0 && (
                            <div className="exec-steps" style={{ marginTop: 12 }}>
                                {adoSync.results.map((result) => (
                                    <div key={result.test_key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0' }}>
                                        <span>
                                            <strong>{result.test_key}</strong>
                                            {result.message ? <span className="exec-step-msg"> - {result.message}</span> : null}
                                        </span>
                                        <Badge bg="" className={
                                            result.status === 'synced'
                                                ? 'exec-badge exec-badge--completed'
                                                : result.status === 'failed'
                                                    ? 'exec-badge exec-badge--failed'
                                                    : 'exec-badge exec-badge--unknown'
                                        }>
                                            {result.status}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    )}

                    {/* Summary stat strip */}
                    {displaySummary && (
                        <div className="exec-stat-strip">
                            <div className="exec-stat">
                                <span className="exec-stat-value">{displaySummary.total_tests ?? '—'}</span>
                                <span className="exec-stat-label">Total</span>
                            </div>
                            <div className="exec-stat exec-stat--pass">
                                <span className="exec-stat-value">{displaySummary.passed ?? '—'}</span>
                                <span className="exec-stat-label">Passed</span>
                            </div>
                            <div className="exec-stat exec-stat--fail">
                                <span className="exec-stat-value">{displaySummary.failed ?? '—'}</span>
                                <span className="exec-stat-label">Failed</span>
                            </div>
                            <div className="exec-stat">
                                <span className="exec-stat-value">
                                    {displaySummary.pass_rate !== undefined && displaySummary.pass_rate !== null
                                        ? `${displaySummary.pass_rate.toFixed(1)}%`
                                        : '—'}
                                </span>
                                <span className="exec-stat-label">Pass Rate</span>
                            </div>
                        </div>
                    )}

                    {/* Per-test results table */}
                    {displaySummary?.test_results && displaySummary.test_results.length > 0 ? (
                        <div className="exec-table-wrapper">
                            <table className="exec-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th style={{ width: 32 }}></th>
                                        <th>Test</th>
                                        <th>Status</th>
                                        <th className="exec-col-num">Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displaySummary.test_results.map((t, idx) => {
                                        const isExpanded = expandedRow === idx;
                                        const hasExtra = t.error || (t.steps && t.steps.length > 0);
                                        return (
                                            <React.Fragment key={idx}>
                                                <tr
                                                    className={`exec-row ${hasExtra ? 'exec-row--clickable' : ''}`}
                                                    onClick={() =>
                                                        hasExtra
                                                            ? setExpandedRow(isExpanded ? null : idx)
                                                            : undefined
                                                    }
                                                >
                                                    <td className="exec-col-icon" data-label="Result">
                                                        {testStatusIcon(t)}
                                                    </td>
                                                    <td className="exec-col-name" data-label="Test">
                                                        <span className="exec-run-name">
                                                            {t.title || t.name || t.test_key || `Test ${idx + 1}`}
                                                        </span>
                                                        {t.test_key && t.title && (
                                                            <span className="exec-base-url">{t.test_key}</span>
                                                        )}
                                                    </td>
                                                    <td data-label="Status">
                                                        {(() => {
                                                            const p = resolvePass(t);
                                                            return (
                                                                <Badge bg="" className={
                                                                    p === true
                                                                        ? 'exec-badge exec-badge--completed'
                                                                        : p === false
                                                                            ? 'exec-badge exec-badge--failed'
                                                                            : 'exec-badge exec-badge--unknown'
                                                                }>
                                                                    {p === true ? 'Passed' : p === false ? 'Failed' : (t.status ?? 'Unknown')}
                                                                </Badge>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="exec-col-num" data-label="Duration">
                                                        {t.duration_ms !== undefined && t.duration_ms !== null
                                                            ? `${(t.duration_ms / 1000).toFixed(1)}s`
                                                            : '—'}
                                                    </td>
                                                </tr>
                                                {isExpanded && hasExtra && (
                                                    <tr className="exec-row exec-row--detail">
                                                        <td colSpan={4}>
                                                            {t.error && (
                                                                <pre className="exec-error-pre">{t.error}</pre>
                                                            )}
                                                            {t.steps && t.steps.length > 0 && (
                                                                <ol className="exec-steps">
                                                                    {t.steps.map((s, si) => {
                                                                        if (typeof s === 'string') {
                                                                            return <li key={si}>{s}</li>;
                                                                        }
                                                                        const step = s as StepResult;
                                                                        return (
                                                                            <li key={si} className={[
                                                                                step.is_assertion ? 'exec-step--assertion' : '',
                                                                                step.success ? 'exec-step--pass' : 'exec-step--fail',
                                                                            ].filter(Boolean).join(' ')}>
                                                                                <span className="exec-step-action">{step.action ?? `Step ${si + 1}`}</span>
                                                                                {step.message && <span className="exec-step-msg"> — {step.message}</span>}
                                                                                {step.error && <div className="exec-step-error">{step.error}</div>}
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ol>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <Alert variant="info" className="mt-3">
                            No per-test breakdown available for this run.
                        </Alert>
                    )}
                </>
            )}
            <RunConfigModal
                show={showRunConfig}
                onHide={() => setShowRunConfig(false)}
                runConfig={detail?.run_config}
                fallbackBaseUrl={detail?.base_url}
                runName={detail?.run_name}
                jobId={detail?.id}
            />
        </div>
    );
}

export default TestExecutionDetail;
